import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { ErrorMessagesRecord, Injectable, ServiceWithErrorMessages } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ErrorWithCode, wrapError } from "@cervval.js/common/src/errors/errors";
import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { DebugCjs, DebugHandler } from "@cervval.js/common/src/tools/debug.decorator";
import { CommonEvent, CommonEventHandler, FullEventInit } from "@cervval.js/common/src/tools/event.tools";
import { anyReallyAny, assertIsNever } from "@cervval.js/common/src/tools/object.tools";
import { ERROR_MESSAGES } from "../../action.errors.i18n";
import { ACTION_STATUS, ACTION_TYPE, Action, ActionInfo, ActionParams, ActionPersistData, ActionPersistResult, DEFAULT_OP_UPDATE_VALUE_CONVERTER, ErrorCodes, OpUpdateValueConverter, OperationType } from "../../action.model";
import { removeInvalidOperations } from "../../action.tools";
import { mapActionInfoToActionPersistData } from "../../mappers/actions.mapper";
import { OpCreate } from "../../operations/opCreate";
import { ACTION_SERVICE, ActionService } from "../action.service";
import { ACTION_PERSISTENCE_SERVICE, ActionPersistenceService } from "../actionPersistence.service";
import { ActionCacheService } from "../cache.service";
import { i18n } from "../i18n/i18n";

@Injectable(ACTION_SERVICE, { registerOnErrorMessageService: true })
export abstract class AbstractActionService<ElementTypes, Context> implements ActionService<ElementTypes, Context>, ServiceWithErrorMessages {

    @DebugCjs(ACTION_SERVICE)
    protected debug!: DebugHandler;

    @Injected(LOG_SERVICE)
    protected readonly _log!: LogService;

    @Injected(ACTION_PERSISTENCE_SERVICE)
    protected readonly _actionPersistenceService!: ActionPersistenceService<Context>;

    /** undo / redo variables */
    protected _undoRedoEnabled = true;
    protected _toUndo: ActionInfo<ElementTypes, Context>[] = [];
    protected _toRedo: ActionInfo<ElementTypes, Context>[] = [];

    /** persist action variables */
    protected _actionsToPersist: ActionInfo<ElementTypes, Context>[] = [];
    protected _sendInProgress: ActionInfo<ElementTypes, Context>[] | undefined;

    /** Convert update values to string (used if the action's logs are enabled)*/
    protected _opUpdateValueConverter = DEFAULT_OP_UPDATE_VALUE_CONVERTER as OpUpdateValueConverter<unknown, Context, ElementTypes, keyof ElementTypes, ElementTypes[keyof ElementTypes], keyof ElementTypes[keyof ElementTypes]>;

    /** Set the converter callback used in all OpUpdates (used if the action's logs are enabled) */
    public set opUpdateValueConverter(converter: OpUpdateValueConverter<unknown, Context, ElementTypes, keyof ElementTypes, ElementTypes[keyof ElementTypes], keyof ElementTypes[keyof ElementTypes]>) {
        this._opUpdateValueConverter = converter;
    }

    /** 
     * clear all lists
     * ! even not persisted actions
     */
    public clearAll(): void {
        this._toUndo.length = 0;
        this._toRedo.length = 0;
        this._actionsToPersist.length = 0;
        this._sendInProgress = undefined;
    }

    /** 
     * clear undo / redo lists
     */
    public clear(): void {
        this._toUndo.length = 0;
        this._toRedo.length = 0;
        this.onClear.emit();
    }

    /** @returns stats on actions to persist and in progress */
    public getPersistQueueStats(): { actionsToPersist: number, operationsToPersist: number, actionsSendInProgress: number, operationsSendInProgress: number } {
        return {
            actionsToPersist: this._actionsToPersist.length,
            operationsToPersist: this._actionsToPersist.reduce((count, ai) => { return ai.action.operations.length + count; }, 0),
            actionsSendInProgress: this._sendInProgress?.length ?? 0,
            operationsSendInProgress: this._sendInProgress ? this._sendInProgress.reduce((count, ai) => { return ai.action.operations.length + count; }, 0) : 0
        };
    }

    /** @returns stats on offline actions to persist and in progress */
    public getOfflinePersistQueueStats(): { actionsToPersist: number, operationsToPersist: number, actionsSendInProgress: number, operationsSendInProgress: number } {
        return {
            actionsToPersist: this._actionsToPersist.filter(a => a.offline).length,
            operationsToPersist: this._actionsToPersist.filter(a => a.offline).reduce((count, ai) => { return ai.action.operations.length + count; }, 0),
            actionsSendInProgress: this._sendInProgress?.filter(a => a.offline).length ?? 0,
            operationsSendInProgress: this._sendInProgress ? this._sendInProgress.filter(a => a.offline).reduce((count, ai) => { return ai.action.operations.length + count; }, 0) : 0
        };
    }

    /** @inheritdoc */
    @CommonEvent(FullEventInit)
    public onAfterActionProcessed!: CommonEventHandler<ActionInfo<ElementTypes, Context>>;

    /** @inheritdoc */
    @CommonEvent(FullEventInit)
    public onClear!: CommonEventHandler<void>;

    /** 
     * Event emitted when persist queue changes (add action, sending actions to persist...)
     */
    @CommonEvent(FullEventInit)
    public onPersistQueueInfoChange!: CommonEventHandler;

    /**
     * Event emitted after all actions have been processed server side and local cache have been updated client side
     */
    @CommonEvent(FullEventInit)
    public afterUpdateLocalCache!: CommonEventHandler<{ actions: Action<ElementTypes, unknown, Context>[], results: ActionPersistResult[] }>;

    /**
     * @returns service related error messages
     */
    public getServiceErrorMessages(): ErrorMessagesRecord {
        return ERROR_MESSAGES;
    }

    /**
     * get current user socket uid
     */
    public getUserSocketUid(): string | undefined {
        return undefined;
    }

    /** @inheritdoc */
    public submitDetachedAction<AppLogInfos, Result>(action: Action<ElementTypes, AppLogInfos, Context>, options?: { result?: Result, doNotRemoveInvalidOperations?: boolean }): Promise<Result> {

        // when using detached action it is easy to get invalid operations (we create or update and object that we delete after) here we remove these invalid operations
        if (!options?.doNotRemoveInvalidOperations) {
            removeInvalidOperations(action);
        }

        const globalCache = this._getGlobalCacheService();
        const tableDefinitionsMap = action.getCacheManager().getTableDefinitionMap();
        if (globalCache) {
            const actionCache = action.getCacheManager();
            if (actionCache !== globalCache) {// If action cache isn't globalCache (=detachedCache)
                // clear all association before redoing all addToArray
                for (const [tableName, tableDef] of tableDefinitionsMap.entries()) {// ? AMT: Why clear all associations of all tables ?
                    for (const column of tableDef.columns) {
                        if (column.isAssociativeColumn) {
                            for (const object of actionCache.getDTOCacheRepository(tableName).dtoList) {
                                if (typeof object !== "object" || object == null) { continue; }
                                setProp(object, column.name, []);// FIXME A discuter
                            }
                        }
                    }
                }
            }
            for (const op of action.operations) {
                if (op.getOperationType() === OperationType.CREATE) { // we need to insert item with the same state as it was inserted in detached cache
                    (op as OpCreate<ElementTypes, anyReallyAny, anyReallyAny, AppLogInfos, Context>).restoreElementToCreationState();
                }
            }
            action.setCacheManager(globalCache);
            // redo all actions to update globalCache
            action.redo();
        }
        return this.submitAction(action, options?.result ?? undefined as Result);// if options.result is not set Result should be void
    }

    /**
     * install a waitForReplyPromise on actionInfo if requested
     */
    protected setupWaitForReply(actionInfo: ActionInfo<ElementTypes, Context>, params?: { waitForReply?: boolean }): Promise<unknown> {
        let waitForReplyPromise: Promise<unknown>;
        // wait if params.waitForReply and return result
        if (params?.waitForReply) {
            this.debug("waiting for reply", actionInfo.action.params.logMessage);
            // register waitForReplyPromise functions, they will be called once the action is persisted
            waitForReplyPromise = new Promise((resolve, reject) => {
                actionInfo.waitForReplyPromise = { resolve, reject };
            });
        } else {
            // do not wait for reply
            waitForReplyPromise = Promise.resolve(null);
        }
        return waitForReplyPromise;
    }

    /** Submit action to server, used by {@link encapsulateInAction} and {@link submitDetachedAction} */
    protected submitAction<AppLogInfos, Result>(action: Action<ElementTypes, AppLogInfos, Context>, result: Result): Promise<Result> {
        let p: Promise<unknown> = Promise.resolve(null);
        const params: ActionParams<ElementTypes, AppLogInfos, Context> = action.params;

        // trigger onAfterProcessOperation
        if (params?.onAfterProcessOperation) {
            p = p.then(() => {
                this.debug("onAfterProcessOperation", action.params.logMessage);
                return params.onAfterProcessOperation!(action, result);
            });
        }

        return p.then(() => {
            if (action.operations.length === 0) {
                // no operation => do not record action and do not send to server
                return undefined;
            }

            // build ActionInfos and register it in the service
            const actionInfo: ActionInfo<ElementTypes, Context> = {
                userSocketUid: this.getUserSocketUid(),
                action: action,
                type: ACTION_TYPE.PROCESS,
                status: ACTION_STATUS.TO_BE_SENT,
                context: action.context
            };
            // add to undo list if undo is enabled and action does not forbid undo registration
            this._addToUndoListIfNeeded(actionInfo);

            // reset toRedo list
            this._toRedo.length = 0;

            // emit processed (usually this will update undo redo buttons states in application)
            this.onAfterActionProcessed.emit(actionInfo);

            // wait if params.waitForReply and return result
            const waitForReplyPromise: Promise<unknown> = this.setupWaitForReply(actionInfo, params);

            // add to persist queue (after setting up actionInfo.waitForReplyPromise to ensure they will be called on persist completion)
            this._addToPersistQueue(actionInfo);

            // process queue to send actions to persist if there is no persistence in progress and we are not offline (otherwise it will be done later)
            this._processPersistQueue();

            // return result
            return waitForReplyPromise;
        }).then(() => result);
    }

    /**
     * Creates an action and once processOperations is finished, sends the operations to be persisted     
     * @param processOperations 
     * @param params 
     * @returns 
     */
    public encapsulateInAction<AppLogInfos, Result>(processOperations: (action: Action<ElementTypes, AppLogInfos, Context>) => Promise<Result> | Result,
        params: ActionParams<ElementTypes, AppLogInfos, Context>): Promise<Result> {

        const isOffline = this._isOffline();
        if (isOffline && params?.waitForReply) {
            const error: ErrorWithCode = { code: ErrorCodes.OFFLINE_CANT_WAIT_FOR_REPLY };
            return Promise.reject(error);
        }

        if (params.opUpdateValueConverter == null) {
            params.opUpdateValueConverter = this._opUpdateValueConverter;
        }

        const action = this._createAction<AppLogInfos>(params);

        return Promise.resolve(undefined).then(() => {
            return processOperations(action);
        }).then((result) => {
            return this.submitAction(action, result);
        });
    }

    /**
     * @returns true if application is offline
     */
    protected _isOffline(): boolean {
        return false;
    }

    /**
     * Create new action from params
     * We are getting context from a contextService
     * @param params 
     * @returns 
     */
    protected abstract _createAction<AppLogInfos>(params: ActionParams<ElementTypes, AppLogInfos, Context>): Action<ElementTypes, AppLogInfos, Context>;

    /**
     * Get the cache service if it exists (will be the cache service on client, undefined on server).
     * It will be used to updated the global cache when submitting a detached action.
     */
    protected abstract _getGlobalCacheService(): ActionCacheService<ElementTypes> | undefined;

    /**
     * Add action to persist queue
     */
    protected _addToPersistQueue(actionInfo: ActionInfo<ElementTypes, Context>): void {
        this.debug("addToPersistQueue", actionInfo);
        this._actionsToPersist.push(actionInfo);
        this.onPersistQueueInfoChange.emit();
    }

    /**
     * Returns the actions to persist
     * @returns {ActionInfo<Context>[]}
     */
    protected _returnActionsToPersistAndEmptyQueue(): ActionInfo<ElementTypes, Context>[] {
        const toSend: ActionInfo<ElementTypes, Context>[] = [];
        for (const actionToSend of this._actionsToPersist) {
            if (actionToSend.status === ACTION_STATUS.TO_BE_SENT) {
                toSend.push(actionToSend);
            } else {
                this.debug("Action is stuck ?", actionToSend);
            }
        }
        this._actionsToPersist = []; // we consider all actions will be persisted
        this.onPersistQueueInfoChange.emit();
        return toSend;
    }

    /**
     * If the queue is not empty and that we can send actions for persistence (ie, there is no persistence in progress and we are not offline) sends the actions
     * otherwise does nothing
     * This function is called as soon as 
     */
    protected _processPersistQueue(): void {
        const isOffline = this._isOffline();
        if (!this._sendInProgress && !isOffline) {
            const actionsToSend = this._returnActionsToPersistAndEmptyQueue();
            if (actionsToSend.length > 0) {
                this._sendActionsToPersistNow(actionsToSend);
            }
        } else {
            this.debug("_processPersistQueue waiting : sendInProgress", this._sendInProgress?.length ?? 0, "- offline", isOffline);
        }
    }

    /**
     * Will transmit the actions to persist to the server and handle cache update and error handling
     * @param actionsToSend 
     * @returns {ActionInfo<Context>[]}
     */
    protected async _sendActionsToPersistNow(actionsToSend: ActionInfo<ElementTypes, Context>[]): Promise<boolean> {
        try {
            this._sendInProgress = actionsToSend;

            this.onPersistQueueInfoChange.emit();

            // set Action status to SEND_IN_PROGRESS
            this.debug("sendActionsToPersistNow", actionsToSend.length + " action(s)");
            for (const actionInfo of actionsToSend) {
                actionInfo.status = ACTION_STATUS.SEND_IN_PROGRESS;
                this.debug(actionInfo.action.params.logMessage, actionInfo);
            }

            // Transform action to send in a list of ActionPersistData
            const actionPersistDataList: ActionPersistData<Context>[] = [];
            for (const actionInfo of actionsToSend) {
                const actionPersistData = mapActionInfoToActionPersistData(actionInfo);
                actionPersistDataList.push(actionPersistData);
            }

            // trigger onBeforePersist
            await Promise.all(actionsToSend
                .filter(actionInfo => !!actionInfo.action.params.onBeforePersist)
                .map(actionInfo => actionInfo.action.params.onBeforePersist!(actionInfo))
            );

            // Persist items in server and wait for result
            const traceInfo = this.debug.traceStart("persistActionData:", actionsToSend.length + " action(s)");
            const persistResultList: ActionPersistResult[] = await this._actionPersistenceService.persistActionData(actionPersistDataList);
            this.debug.traceFinish(traceInfo);

            // set Action status to SENT
            for (const actionInfo of actionsToSend) {
                actionInfo.status = ACTION_STATUS.SENT;
            }

            // Get the list of the persisted actions
            const persistedActions = actionsToSend.map(actionInfo => actionInfo.action);

            // Trigger onAfterPersist for each action if onAfterPersist is defined
            const onAfterPersistPromiseList = [];
            for (let index = 0; index < actionsToSend.length; index++) {
                const actionInfo = actionsToSend[index];
                const persistResult = persistResultList[index];
                if (!actionInfo.action.params.onAfterPersist) {
                    continue;
                }
                const onAfterPersistPromise = actionInfo.action.params.onAfterPersist(actionInfo.action, persistedActions, persistResult);
                onAfterPersistPromiseList.push(onAfterPersistPromise);
            }
            await Promise.all(onAfterPersistPromiseList);

            // Trigger onAfterPersist for each action if onAfterPersist is defined
            const onAfterUpdateLocalCachePromiseList = [];
            for (let index = 0; index < actionsToSend.length; index++) {
                const actionInfo = actionsToSend[index];
                const persistResult = persistResultList[index];
                // Update cache from server result
                this.debug("updatePersistedElements", actionInfo.action.params.logMessage, persistResult);
                actionInfo.action.getCacheManager().updatePersistedElements(persistResult);

                if (!actionInfo.action.params.onAfterUpdateLocalCache) {
                    continue;
                }
                const onAfterUpdateLocalCachePromise = actionInfo.action.params.onAfterUpdateLocalCache(persistedActions, persistResult);
                onAfterUpdateLocalCachePromiseList.push(onAfterUpdateLocalCachePromise);
            }
            await Promise.all(onAfterUpdateLocalCachePromiseList);

            this.afterUpdateLocalCache.emit({ actions: persistedActions, results: persistResultList });

            // Manage error and promise if the user is waiting for a reply
            const onPersistErrorPromiseList = [];
            for (let index = 0; index < actionsToSend.length; index++) {
                const actionInfo = actionsToSend[index];
                const persistResult = persistResultList[index];
                if (!actionInfo.action.params.waitForReply && persistResult.error && actionInfo.action.params.onPersistError) {
                    actionInfo.status = ACTION_STATUS.ERROR;
                    const onAfterPersistPromise = actionInfo.action.params.onPersistError(actionInfo, actionsToSend, persistResult);
                    onPersistErrorPromiseList.push(onAfterPersistPromise);
                }
                else if (!persistResult.error) {
                    actionInfo.waitForReplyPromise?.resolve(actionInfo);
                } else {
                    if (actionInfo.waitForReplyPromise) {
                        actionInfo.waitForReplyPromise?.reject(persistResult.error);
                    } else {
                        onPersistErrorPromiseList.push(Promise.reject(persistResult.error));// ensure error is sent to user
                    }
                }
            }
            await Promise.all(onPersistErrorPromiseList);

            return true;
        } catch (err) {
            this.debug("sendActionsToPersistNow.error", err);
            const error = wrapError(err);
            return this._handleError(actionsToSend, error);
        } finally {
            this._sendInProgress = undefined;

            this.onPersistQueueInfoChange.emit();

            // Process the persist Queue
            this._processPersistQueue();
        }
    }

    /**
     * Trigger the onPersistError method if implemented for the actions
     * @param { ActionInfo<Context>[] } actions 
     * @param {ErrorWithCode} error 
     * @returns Promise with boolean to indicate if error has been handled
     */
    protected _handleError(actions: ActionInfo<ElementTypes, Context>[], error: ErrorWithCode): Promise<boolean> {
        this._log.error(error);

        let errorP: Promise<boolean> = Promise.resolve(false);
        for (const actionInfo of actions) {
            if (actionInfo.action.params.onPersistError) {
                errorP = errorP.then((errorHandled: boolean) => {
                    if (!errorHandled) {
                        return actionInfo.action.params.onPersistError!(actionInfo, actions, error);
                    }
                    return false;
                });
            }
        }

        // set ERROR status
        errorP = errorP.then((errorHandled: boolean) => {
            for (const actionInfo of actions) {
                actionInfo.status = ACTION_STATUS.ERROR;
            }
            return errorHandled;
        });

        return errorP;
    }

    //#region Undo Redo

    /**
     * @returns true if undo/redo is enabled and there is at least one action to undo
     */
    public canUndo(): boolean {
        return this.getNextActionToUndo() != null;
    }

    /**
     * @returns the next ActionInfo that can be undone
     */
    public getNextActionToUndo(): ActionInfo<ElementTypes, Context> | undefined {
        if (!this._undoRedoEnabled) { return undefined; }
        return this._toUndo[this._toUndo.length - 1];
    }

    /**
     * @returns true if undo/redo is enabled and there is at least one action to redo
     */
    public canRedo(): boolean {
        return this.getNextActionToRedo() != null;
    }

    /**
     * @returns the next ActionInfo that can be redone
     */
    public getNextActionToRedo(): ActionInfo<ElementTypes, Context> | undefined {
        if (!this._undoRedoEnabled) { return undefined; }
        return this._toRedo[this._toRedo.length - 1];
    }

    /**
     * Add the action to the undo list if undo is enabled and action does not forbid undo registration
     */
    protected _addToUndoListIfNeeded(actionInfo: ActionInfo<ElementTypes, Context>): void {
        if (this._undoRedoEnabled && !actionInfo.action.params.doNotRegisterForUndo) {
            this.debug("adding action to undo list", actionInfo.action.params.logMessage);
            this._toUndo.push(actionInfo);
        }
    }

    /**
     * Undo the last action found in this._toUndo
     * Does not handle errors, you need to wrap with try catch
     * @inheritdoc
     */
    public undo(): void {
        this._do({ actionList: this._toUndo, actionType: ACTION_TYPE.UNDO, addNewActionInfoToList: this._toRedo, waitForReply: false });
    }

    /**
     * Undo the last action found in this._toUndo and wait for server reply
     * Does not handle errors, you need to catch them
     */
    public undoAndWaitForReply(): Promise<void> {
        return Promise.resolve(this._do({ actionList: this._toUndo, actionType: ACTION_TYPE.UNDO, addNewActionInfoToList: this._toRedo, waitForReply: true }));
    }

    /**
     * Redo the last action found in this._toRedo
     * Does not handle errors, you need to wrap with try catch
     * @inheritdoc
     */
    public redo(): void {
        this._do({ actionList: this._toRedo, actionType: ACTION_TYPE.REDO, addNewActionInfoToList: this._toUndo, waitForReply: false });
    }

    /**
     * Redo the last action found in this._toRedo and wait for server reply
     * Does not handle errors, you need to catch them
     */
    public redoAndWaitForReply(): Promise<void> {
        return Promise.resolve(this._do({ actionList: this._toRedo, actionType: ACTION_TYPE.REDO, addNewActionInfoToList: this._toUndo, waitForReply: true }));
    }

    /**
     * Undo or redo last action in list
     * @returns a Promise if p.waitForReply and there is something to do, undefined otherwise
     */
    protected _do(p: {
        actionList: ActionInfo<ElementTypes, Context>[],
        actionType: ACTION_TYPE.REDO | ACTION_TYPE.UNDO,
        addNewActionInfoToList?: ActionInfo<ElementTypes, Context>[],
        waitForReply?: boolean
    }): Promise<void> | undefined {
        if (!this._undoRedoEnabled) { return undefined; }

        const actionInfo = p.actionList.pop();
        if (actionInfo === undefined) {
            // nothing to do
            return undefined;
        }

        // Retrieve i18n tokens
        const actionI18n = i18n();

        // Define message of action type (undo/redo)
        const actionTypeMessage = p.actionType === ACTION_TYPE.REDO ? actionI18n.redo : actionI18n.undo;

        // Add "undo" or "redo" to previous action logMessage
        const newLogMessage = `${actionTypeMessage} : '${actionInfo.action.params.logMessage}'`;

        // Set new log message
        actionInfo.action.params.logMessage = newLogMessage;

        this.debug(p.actionType, newLogMessage);

        // create a new ActionInfo with UNDO or REDO type
        const newActionInfo: ActionInfo<ElementTypes, Context> = {
            userSocketUid: this.getUserSocketUid(),
            action: actionInfo.action,
            type: p.actionType,
            status: ACTION_STATUS.TO_BE_SENT,
            context: actionInfo.context
        };

        // process operations
        if (p.actionType === ACTION_TYPE.REDO) {
            newActionInfo.action.redo();
        } else if (p.actionType === ACTION_TYPE.UNDO) {
            newActionInfo.action.undo();
        } else {
            assertIsNever(p.actionType);
        }

        // add to undo or redo list
        p.addNewActionInfoToList?.push(newActionInfo);

        // emit processed (usually this will update undo redo buttons states in application)
        this.onAfterActionProcessed.emit(newActionInfo);

        // wait if params.waitForReply and return result
        const waitForReplyPromise: Promise<unknown> | undefined = p.waitForReply ? this.setupWaitForReply(newActionInfo, { waitForReply: true }) : undefined;

        // add to persist queue (after setting up actionInfo.waitForReplyPromise to ensure they will be called on persist completion)
        this._addToPersistQueue(newActionInfo);

        // process queue to send actions to persist if there is no persistence in progress and we are not offline (otherwise it will be done later)
        this._processPersistQueue();

        // return result
        return waitForReplyPromise?.then(() => undefined);
    }

    //#endregion Undo Redo

    /**
     * Wait the persistence queue is empty. 
     * Useful for testing action 
     * @returns 
     */
    public waitForPersistenceQueueEmpty(): Promise<void> {
        if (this.isPersistenceQueueEmpty()) {
            // No need to wait
            return Promise.resolve();
        } else {
            const p: Promise<() => void> = new Promise((resolve, reject) => {
                const f = (): void => {
                    if (this.isPersistenceQueueEmpty()) {
                        resolve(f);
                    } else {
                        // Not finished yet
                    }
                };
                // Register callback
                this.onPersistQueueInfoChange.on(f);
            });
            return p.then(f => {
                // Unregister callback
                this.onPersistQueueInfoChange.off(f);
            });
        }
    }

    /**
     * Check if the persist queue is empty and there is no action in progress
     */
    public isPersistenceQueueEmpty(): boolean {
        const info = this.getPersistQueueStats();
        if (info.actionsToPersist === 0 && info.actionsSendInProgress === 0) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Check if the offline persist queue is empty and there is no offline action in progress
     */
    public isOfflinePersistenceQueueEmpty(): boolean {
        const info = this.getOfflinePersistQueueStats();
        if (info.actionsToPersist === 0 && info.actionsSendInProgress === 0) {
            return true;
        } else {
            return false;
        }
    }
}